import { useEffect, useRef, useState } from 'react'

import {
  type MaterialLearner,
  type MaterialPracticeSummary,
  type StartFailure,
  StartLearningError,
} from '@/platform/materials/learning'
import { newUuid } from '@/shared/id/uuid'
import {
  ExtractionError,
  type ExtractionFailure,
  type MaterialReader,
} from '@/platform/materials/extraction'

import {
  checkContent,
  checkFile,
  FILE_ERROR_TEXT,
  forUpload,
  type MaterialKind,
} from './file'
import { hasFractionTopic } from './fraction-expressions'
import { LearnStep } from './LearnStep'
import { FilePreview, type LocalMaterial, MaterialEntry } from './MaterialEntry'
import {
  type Attempt,
  type DemoRunReport,
  type HelpMode,
  type LoadSampleMaterials,
  type SampleMaterial,
  type SelfReport,
  type Topic,
} from './model'
import { MaterialLesson } from './MaterialLesson'
import { PracticeStep } from './PracticeStep'
import {
  ConfirmedContent,
  ExtractionReview,
  type ReaderState,
  ReadingFailed,
  UploadClosed,
  ReadingProgress,
  SendForReading,
} from './ReadMaterial'
import { ResultStep } from './ResultStep'
import {
  confirm,
  type ConfirmedMaterial,
  resolve,
  type ReviewState,
  startReview,
} from './review'
import { ContentReview, SamplePicker, TopicSelect } from './ReviewSteps'
import { sendableSubject, type SubjectChoice } from './subject'
import { type FallbackReason, TopicConfirm, TopicFallback } from './TopicCheck'

/**
 * School Material Learning Journey (Slice 2, D-14.3), reached from Child Home:
 *
 *   Material → Preview → Content Review → Topic → Learn → Practice → Result
 *   → Next Learning Action
 *
 * A real photo or PDF (Slice 3, Mathematics only) is previewed locally and,
 * when reading is available, sent by the learner to the `extract-material`
 * function through `reader`:
 *
 *   Material → Preview → Send → Reading → Review against the original →
 *   Confirm (or reject) → Confirmed content
 *
 * Uncertain and unreadable parts must be checked, corrected or left out before
 * anything can be confirmed. The subject is the learner's explicit choice
 * (never a default; "another subject" sends nothing). For Mathematics the
 * journey then continues concept-first (D-21): Confirmed content → Topic
 * confirmation → Lesson (Thinking Toolbox, two independent practice
 * questions, feedback, summary). After the topic is confirmed nothing leads
 * back to the original (User Features UP-05, UP-09); when the topic is
 * not recognised, rejected, or no independent practice can be made, a
 * fallback explains why and shows the original, with no practice and no
 * evidence. Without a reader, or without a configured provider, the preview
 * says reading is not available.
 *
 * The full journey runs only on DEMO samples, offered when `loadSamples` is
 * given (local/development, D-02). The journey never receives the learning
 * service or the learner's ID (`reader` is already bound to the learner), so
 * nothing done here can reach real learner records; a finished DEMO run is
 * only handed to `onDemoRun` for the in-memory parent preview, without any
 * learner identifier.
 */

export interface SchoolMaterialJourneyProps {
  /** DEMO samples; null in builds without demo content. */
  readonly loadSamples: LoadSampleMaterials | null
  /** Receives each finished DEMO run (parent preview, memory only). */
  readonly onDemoRun: (report: DemoRunReport) => void
  readonly onHome: () => void
  /** Open on the sample list instead of the material choice (needs `loadSamples`). */
  readonly startWithSamples?: boolean
  /** Reads real material for this learner (Slice 3); null where reading may not run. */
  readonly reader?: MaterialReader | null
  /** Receives confirmed real content. */
  readonly onConfirmed?: (confirmed: ConfirmedMaterial) => void
  /** Lessons from confirmed material (D-17), bound to the learner session. */
  readonly learner?: MaterialLearner | null
}

type Stage =
  | 'entry'
  | 'file'
  | 'reading'
  | 'read_failed'
  | 'read_review'
  | 'confirmed'
  | 'topic_confirm'
  | 'topic_fallback'
  | 'lesson'
  | 'samples'
  | 'review'
  | 'topic'
  | 'learn'
  | 'practice'
  | 'result'

const LEARN_ERROR_TEXT: Readonly<Record<StartFailure, string>> = {
  unsupported_topic:
    'Ứng dụng mới học được phần cộng, trừ phân số từ tài liệu Toán. Nội dung này chưa có phân số nào như vậy.',
  practice_unavailable:
    'Ứng dụng chưa tạo được bài luyện đủ khác với bài trong tài liệu của con.',
  too_little_text:
    'Nội dung này quá ngắn để biết là về chủ đề nào. Con thêm hoặc chọn tài liệu có nhiều câu hơn nhé.',
  content_not_reviewed:
    'Bài luyện tiếng Anh này chưa được thầy cô duyệt nên chưa dùng được ở đây.',
  learner_session_invalid: 'Con cần nhập lại mã PIN của con rồi thử lại nhé.',
  upload_not_found: 'Không tìm thấy lần gửi tài liệu này. Con gửi lại tài liệu nhé.',
  bad_request: 'Nội dung chưa hợp lệ để học. Con kiểm tra lại các phần nhé.',
  service_unavailable: 'Chưa chuẩn bị được bài học. Con thử lại sau một lúc nhé.',
  network: 'Mất kết nối. Con kiểm tra mạng rồi thử lại nhé.',
}

/** Tiếng Anh: chủ đề duy nhất hiện có là School places (các nơi trong trường). */
const ENGLISH_UNSUPPORTED_TEXT =
  'Ứng dụng mới học được chủ đề School places (các nơi trong trường) từ tài liệu tiếng Anh. Nội dung này chưa nói về các nơi trong trường, nên chưa tạo bài luyện được.'

/** Only topic choice may reopen the material; learning steps never do (UP-09). */
type ReturnStage = 'topic'

interface Practice {
  readonly index: number
  readonly attempts: ReadonlyMap<string, Attempt>
  readonly help: ReadonlyMap<string, readonly HelpMode[]>
}

const NEW_PRACTICE: Practice = { index: 0, attempts: new Map(), help: new Map() }

export function SchoolMaterialJourney({
  loadSamples,
  onDemoRun,
  onHome,
  startWithSamples = false,
  reader = null,
  onConfirmed,
  learner = null,
}: SchoolMaterialJourneyProps): React.JSX.Element {
  const [stage, setStage] = useState<Stage>(
    startWithSamples && loadSamples !== null ? 'samples' : 'entry',
  )
  const [local, setLocal] = useState<LocalMaterial | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [sample, setSample] = useState<SampleMaterial | null>(null)
  const [topic, setTopic] = useState<Topic | null>(null)
  const [selfReport, setSelfReport] = useState<SelfReport | null>(null)
  const [practice, setPractice] = useState<Practice>(NEW_PRACTICE)
  const [returnTo, setReturnTo] = useState<ReturnStage | null>(null)

  // Slice 3: reading real material.
  const [readerState, setReaderState] = useState<ReaderState>(
    reader === null ? { available: false, why: 'disabled' } : { kind: 'checking' },
  )
  const [recheck, setRecheck] = useState(0)
  const [failure, setFailure] = useState<Exclude<ExtractionFailure, 'cancelled'> | null>(
    null,
  )
  const [restrictedUntil, setRestrictedUntil] = useState<string | null>(null)
  // One per chosen file; a retry reuses it so the server counts the upload once.
  const [requestId, setRequestId] = useState<string | null>(null)
  const [readCancelled, setReadCancelled] = useState(false)
  const [review, setReview] = useState<ReviewState | null>(null)
  // The subject the learner says this material is for: none until they choose (D-21).
  const [subject, setSubject] = useState<SubjectChoice | null>(null)
  const [fallback, setFallback] = useState<FallbackReason>('not_recognised')
  const [confirmed, setConfirmed] = useState<ConfirmedMaterial | null>(null)
  // D-17: the persisted lesson, its start, and lessons to resume.
  const [lessonId, setLessonId] = useState<string | null>(null)
  const [learnBusy, setLearnBusy] = useState(false)
  const [learnError, setLearnError] = useState<string | null>(null)
  const [resumable, setResumable] = useState<MaterialPracticeSummary[]>([])

  useEffect(() => {
    if (learner === null) return
    let active = true
    learner.listMine().then(
      (list) => {
        if (active)
          setResumable(
            list.filter((l) => l.completed_at === null && l.answered < l.items),
          )
      },
      () => undefined,
    )
    return () => {
      active = false
    }
  }, [learner, lessonId])
  // Only the latest pick or reading may change the screen; stale ones are ignored.
  const pickToken = useRef(0)
  const reading = useRef<AbortController | null>(null)

  useEffect(() => {
    if (reader === null) return
    let active = true
    reader.availability().then(
      (a) => {
        if (active) setReaderState(a)
      },
      () => {
        if (active) setReaderState({ available: false, why: 'unreachable' })
      },
    )
    return () => {
      active = false
    }
  }, [reader, recheck])

  // Sent only as a subject the learner chose and the server offers; never a
  // silent default. The server enforces availability anyway.
  const sendSubject = sendableSubject(readerState, subject)

  // Leaving the journey stops a reading in progress.
  useEffect(
    () => () => {
      reading.current?.abort()
    },
    [],
  )

  const stopReading = () => {
    reading.current?.abort()
    reading.current = null
  }

  const resetRead = () => {
    stopReading()
    setFailure(null)
    setRestrictedUntil(null)
    setReadCancelled(false)
    setReview(null)
    setConfirmed(null)
  }

  // The object URL of the previewed file is revoked when the file is replaced
  // or removed, and when the journey closes.
  const localUrl = useRef<string | null>(null)
  useEffect(
    () => () => {
      if (localUrl.current !== null) URL.revokeObjectURL(localUrl.current)
    },
    [],
  )
  const replaceLocal = (next: LocalMaterial | null) => {
    if (localUrl.current !== null) URL.revokeObjectURL(localUrl.current)
    localUrl.current = next?.url ?? null
    setLocal(next)
  }

  const pick = (kind: MaterialKind, file: File) => {
    const token = ++pickToken.current
    const check = checkFile(file, kind)
    if (!check.ok) {
      setFileError(FILE_ERROR_TEXT[check.error])
      return
    }
    void checkContent(file, kind).then((intact) => {
      if (token !== pickToken.current) return
      if (!intact) {
        setFileError(FILE_ERROR_TEXT.corrupted)
        return
      }
      resetRead()
      replaceLocal({ file, kind, url: URL.createObjectURL(file) })
      setRequestId(newUuid())
      setSubject(null)
      setFileError(null)
      setStage('file')
    })
  }

  const removeFile = () => {
    pickToken.current++
    resetRead()
    replaceLocal(null)
    setFileError(null)
    setStage('entry')
  }

  const send = () => {
    if (local === null || !('available' in readerState) || !readerState.available) return
    if (reader === null || sendSubject === null) return
    const subjectToSend = sendSubject
    stopReading()
    const controller = new AbortController()
    reading.current = controller
    setFailure(null)
    setReadCancelled(false)
    setStage('reading')
    const isCurrent = () => reading.current === controller && !controller.signal.aborted
    void forUpload(local.file)
      .then((file) =>
        reader.extract({
          file,
          subject: subjectToSend,
          // Only a subject the server offers solely as the local fixture is asked that way.
          ...(readerState.syntheticFixtureSubjects?.includes(subjectToSend) === true
            ? { syntheticFixture: true }
            : {}),
          requestId: requestId ?? newUuid(),
          signal: controller.signal,
        }),
      )
      .then(
        (extraction) => {
          if (!isCurrent()) return
          reading.current = null
          setReview(startReview(extraction))
          setStage('read_review')
        },
        (error: unknown) => {
          if (!isCurrent()) return
          reading.current = null
          const code = error instanceof ExtractionError ? error.code : 'network'
          if (code === 'cancelled') {
            setStage('file')
            return
          }
          const until = error instanceof ExtractionError ? error.restrictedUntil : null
          setFailure(code)
          setRestrictedUntil(until)
          // Upload just closed for this learner: the entry screen must say so too.
          if (code === 'not_entitled') setReaderState({ available: false, why: code })
          else if (code === 'consent_required')
            setReaderState({ available: false, why: code })
          else if (
            code === 'restricted' ||
            (code === 'not_learning_material' && until !== null)
          )
            setReaderState({
              available: false,
              why: 'restricted',
              restrictedUntil: until,
            })
          setStage('read_failed')
        },
      )
  }

  const cancelReading = () => {
    stopReading()
    setReadCancelled(true)
    setStage('file')
  }

  const openSamples = () => {
    setFileError(null)
    setStage('samples')
  }

  const openSource = (from: ReturnStage) => {
    setReturnTo(from)
    setStage('review')
  }

  const startPractice = () => {
    setPractice(NEW_PRACTICE)
    setStage('practice')
  }

  const showFallback = (reason: FallbackReason) => {
    setFallback(reason)
    setStage('topic_fallback')
  }

  /** Saves the confirmed source and opens the lesson the server prepares from it. */
  const startLesson = (c: ConfirmedMaterial) => {
    if (learner === null || requestId === null) return
    setLearnBusy(true)
    setLearnError(null)
    learner
      .start({
        request_id: requestId,
        ...(c.subject === 'english' ? { subject: 'english' as const } : {}),
        source: {
          document_sha256: c.source.documentSha256,
          mime_type: c.source.mimeType,
          page_count: c.source.pageCount,
          provider_id: c.extraction.provider.id,
          extraction_id: c.extraction.id,
          extracted_at: c.extraction.extractedAt,
        },
        blocks: [
          ...c.blocks.map((b) => ({
            block_ref: b.blockId,
            page: b.page,
            region: b.region,
            kind: b.kind,
            extracted_status: b.extractedStatus,
            resolution: b.resolution,
            text: b.text,
          })),
          ...c.excluded.map((b) => ({
            block_ref: b.blockId,
            page: b.page,
            region: null,
            kind: 'text',
            extracted_status: b.extractedStatus,
            resolution: 'excluded' as const,
            text: null,
          })),
        ],
      })
      .then(
        (setId) => {
          setLessonId(setId)
          setStage('lesson')
        },
        (e: unknown) => {
          const code = e instanceof StartLearningError ? e.code : 'network'
          if (c.subject === 'english') {
            setLearnError(
              code === 'unsupported_topic'
                ? ENGLISH_UNSUPPORTED_TEXT
                : LEARN_ERROR_TEXT[code],
            )
            return
          }
          // The server decided with the same rules: never a dead end.
          if (code === 'unsupported_topic') showFallback('not_recognised')
          else if (code === 'practice_unavailable') showFallback('no_practice')
          else setLearnError(LEARN_ERROR_TEXT[code])
        },
      )
      .finally(() => {
        setLearnBusy(false)
      })
  }

  const canRead = 'available' in readerState && readerState.available

  if (stage === 'lesson' && lessonId !== null && learner !== null) {
    return (
      <MaterialLesson
        learner={learner}
        setId={lessonId}
        onHome={onHome}
        onNewMaterial={() => {
          setLessonId(null)
          removeFile()
        }}
      />
    )
  }

  if (local !== null) {
    switch (stage) {
      case 'file':
        return (
          <FilePreview
            material={local}
            error={fileError}
            canRead={canRead}
            reading={
              <SendForReading
                reader={readerState}
                cancelled={readCancelled}
                samplesAvailable={loadSamples !== null}
                onSend={send}
                subject={subject}
                onSubject={setSubject}
                onRecheck={() => {
                  setReaderState({ kind: 'checking' })
                  setRecheck((n) => n + 1)
                }}
                onSamples={openSamples}
              />
            }
            onPick={pick}
            onRemove={removeFile}
            onHome={onHome}
          />
        )

      case 'reading':
        return (
          <ReadingProgress material={local} onCancel={cancelReading} onHome={onHome} />
        )

      case 'read_failed':
        if (failure === null) break
        return (
          <ReadingFailed
            failure={failure}
            restrictedUntil={restrictedUntil}
            onRetry={send}
            onChooseAnother={removeFile}
            onHome={onHome}
          />
        )

      case 'read_review':
        if (review === null) break
        return (
          <ExtractionReview
            material={local}
            review={review}
            onResolve={(blockId, resolution) => {
              setReview((r) => (r === null ? r : resolve(r, blockId, resolution)))
            }}
            onConfirm={() => {
              const result = confirm(review, local.file.name, new Date())
              if (result === null) return
              setConfirmed(result)
              onConfirmed?.(result)
              setStage('confirmed')
            }}
            onReject={removeFile}
            onHome={onHome}
          />
        )

      case 'confirmed':
        if (confirmed === null) break
        return (
          <ConfirmedContent
            confirmed={confirmed}
            onChooseAnother={removeFile}
            onHome={onHome}
            learnBusy={learnBusy}
            learnError={learnError}
            {...(learner === null || requestId === null
              ? {}
              : {
                  onLearn: () => {
                    if (confirmed.subject === 'english') {
                      startLesson(confirmed)
                      return
                    }
                    // Concept-first Mathematics (D-21): name the recognised
                    // topic and ask first; otherwise the honest fallback.
                    setLearnError(null)
                    if (
                      hasFractionTopic(
                        confirmed.blocks.map((b) => ({ ref: b.blockId, text: b.text })),
                      )
                    )
                      setStage('topic_confirm')
                    else showFallback('not_recognised')
                  },
                })}
          />
        )

      case 'topic_confirm':
        if (confirmed === null) break
        return (
          <TopicConfirm
            busy={learnBusy}
            error={learnError}
            onConfirm={() => {
              startLesson(confirmed)
            }}
            onReject={() => {
              showFallback('rejected')
            }}
            onHome={onHome}
          />
        )

      case 'topic_fallback':
        return (
          <TopicFallback
            reason={fallback}
            material={local}
            onChooseAnother={removeFile}
            onHome={onHome}
          />
        )

      default:
        break
    }
  }

  if (stage === 'samples' && loadSamples !== null) {
    return (
      <SamplePicker
        load={loadSamples}
        onChoose={(next) => {
          setSample(next)
          setTopic(null)
          setSelfReport(null)
          setPractice(NEW_PRACTICE)
          setReturnTo(null)
          setStage('review')
        }}
        onBack={() => {
          setStage(local === null ? 'entry' : 'file')
        }}
        onHome={onHome}
      />
    )
  }

  if (sample !== null) {
    const back = returnTo
    switch (stage) {
      case 'review':
        return (
          <ContentReview
            sample={sample}
            onContinue={() => {
              setStage('topic')
            }}
            onReturn={
              back === null
                ? null
                : () => {
                    setReturnTo(null)
                    setStage(back)
                  }
            }
            onBack={openSamples}
            onHome={onHome}
          />
        )

      case 'topic':
        return (
          <TopicSelect
            sample={sample}
            onChoose={(next) => {
              if (next.id !== topic?.id) {
                setSelfReport(null)
                setPractice(NEW_PRACTICE)
              }
              setTopic(next)
              setStage('learn')
            }}
            onOpenSource={() => {
              openSource('topic')
            }}
            onBack={() => {
              setStage('review')
            }}
            onHome={onHome}
          />
        )

      default:
        break
    }

    if (topic !== null) {
      switch (stage) {
        case 'learn':
          return (
            <LearnStep
              sample={sample}
              topic={topic}
              selfReport={selfReport}
              onSelfReport={setSelfReport}
              onPractice={() => {
                // Continue an unfinished practice; start again after a finished one.
                if (practice.attempts.size === topic.questions.length) startPractice()
                else setStage('practice')
              }}
              onBack={() => {
                setStage('topic')
              }}
              onHome={onHome}
            />
          )

        case 'practice':
          return (
            <PracticeStep
              sample={sample}
              topic={topic}
              index={practice.index}
              attempts={practice.attempts}
              help={practice.help}
              onHelp={(questionId, mode) => {
                setPractice((p) => {
                  const used = p.help.get(questionId) ?? []
                  if (used.includes(mode) || p.attempts.has(questionId)) return p
                  return { ...p, help: new Map(p.help).set(questionId, [...used, mode]) }
                })
              }}
              onSubmitted={(attempt) => {
                setPractice((p) =>
                  p.attempts.has(attempt.questionId)
                    ? p
                    : {
                        ...p,
                        attempts: new Map(p.attempts).set(attempt.questionId, attempt),
                      },
                )
              }}
              onNext={() => {
                if (practice.index < topic.questions.length - 1) {
                  setPractice({ ...practice, index: practice.index + 1 })
                  return
                }
                onDemoRun({
                  sample,
                  topic,
                  attempts: [...practice.attempts.values()],
                  selfReport,
                })
                setStage('result')
              }}
              onBack={() => {
                if (practice.index > 0)
                  setPractice({ ...practice, index: practice.index - 1 })
                else setStage('learn')
              }}
              onHome={onHome}
            />
          )

        case 'result':
          return (
            <ResultStep
              sample={sample}
              topic={topic}
              attempts={[...practice.attempts.values()]}
              selfReport={selfReport}
              onAction={(kind) => {
                if (kind === 'review_explanation') {
                  setPractice(NEW_PRACTICE)
                  setStage('learn')
                } else if (kind === 'practise_again') {
                  startPractice()
                } else {
                  openSamples()
                }
              }}
              onBack={() => {
                setStage('practice')
              }}
              onHome={onHome}
            />
          )

        default:
          break
      }
    }
  }

  return (
    <MaterialEntry
      error={fileError}
      samplesAvailable={loadSamples !== null}
      canRead={canRead}
      resume={
        resumable.length === 0 ? null : (
          <section aria-labelledby="resume" className="flex flex-col gap-2">
            <h2 id="resume" className="text-lg font-semibold">
              Bài đang học dở
            </h2>
            {resumable.map((r) => (
              <button
                key={r.set_id}
                type="button"
                className="flex min-h-14 items-center justify-between gap-3 rounded-2xl border-2 border-emerald-300 bg-white px-4 text-left text-lg dark:border-emerald-800 dark:bg-slate-900"
                onClick={() => {
                  setLessonId(r.set_id)
                  setStage('lesson')
                }}
              >
                <span>{r.topic_label}</span>
                <span className="text-base text-slate-600 dark:text-slate-400">
                  {r.answered}/{r.items} câu
                </span>
              </button>
            ))}
          </section>
        )
      }
      closed={
        'available' in readerState &&
        !readerState.available &&
        (readerState.why === 'not_entitled' || readerState.why === 'restricted') ? (
          <UploadClosed reader={readerState} onLearn={onHome} />
        ) : null
      }
      onPick={pick}
      onSamples={openSamples}
      onHome={onHome}
    />
  )
}
